import React, { useMemo, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Pagination, Row, Space, Table, Typography, message } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./CombustionTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { settingStore } from "src/stores/setting-store";
import { useShallow } from "zustand/react/shallow";
import { PlusCircleOutlined } from "@ant-design/icons";
import { generateErrorMessage } from "src/utils/error-message";
import VocsModal from "src/components/Modal";
import { replaceUndefinedWithNull, trimAllKey } from "src/utils/utils-object";
import {
  CreateSettingWasteWaterTreatment,
  FilterSettingWasteWaterTreatment,
  ResponseSettingWasteWaterTreatment,
  UpdateSettingWasteWaterTreatment,
} from "src/entity/settings/waste-water";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import { canEditTable } from "./PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { reportWasteWaterStore } from "src/stores/waste-water-report";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { formatNumber, formatNumberWithFillZero } from "src/utils/process-number";

const { Text } = Typography;
interface WasteWaterTableProps {}

const WasteWaterTable: React.FC<WasteWaterTableProps> = ({}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [formSettingWasteWater] = Form.useForm();
  const [visibleModal, setVisibleModal] = useState(false);
  const getSettingWasteWater = settingStore(useShallow((state) => state.getSettingWasteWater));
  const dataSettingWasteWater = settingStore(useShallow((state) => state.dataSettingWasteWater));
  const totalDocs = dataSettingWasteWater.total;
  const [isEdit, setIsEdit] = useState<UpdateSettingWasteWaterTreatment | null>(null);
  const { permissions } = useNavigationMenu();

  //   const profile = profileStore(useShallow((state) => state.profile));
  //   const companies = profile?.company ?? [];
  //   const emissionData = settingStore(useShallow((state) => state.emission));
  //   const unitData = settingStore(useShallow((state) => state.unit));
  const getFilterWasteWaterColumn = settingStore(useShallow((state) => state.getFilterWasteWaterColumn));
  const filterWasteWater = settingStore(useShallow((state) => state.filterWasteWater));
  const loadingManageWasteWater = settingStore(useShallow((state) => state.loadingManageWasteWater));
  const createSettingWasteWater = settingStore(useShallow((state) => state.createSettingWasteWater));
  const deleteSettingWasteWater = settingStore(useShallow((state) => state.deleteSettingWasteWater));
  const updateSettingWasteWater = settingStore(useShallow((state) => state.updateSettingWasteWater));
  const clearErrorSettingWasteWater = settingStore(useShallow((state) => state.clearErrorSettingWasteWater));
  const errorManageSettingWater = settingStore(useShallow((state) => state.errorManageWasteWater));
  const calculateReportVocAfterUpdateMasterWW = reportWasteWaterStore(useShallow((state) => state.calculateReportVocAfterUpdateMasterWW));

  const onClickEdit = (record: ResponseSettingWasteWaterTreatment) => {
    setIsEdit(record);
    setVisibleModal(true);
    formSettingWasteWater.setFieldsValue({
      ...record,
    });
  };
  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });

    if (!confirm) return;
    deleteSettingWasteWater(id).then(() => onFinishAction());
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
  } = usePagination<ResponseSettingWasteWaterTreatment, FilterSettingWasteWaterTreatment>({
    action: getSettingWasteWater,
    remoteData: dataSettingWasteWater.data,
  });

  const emissionControlFilter = useMemo(() => {
    return filterWasteWater.emissionControl.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterWasteWater.emissionControl]);
  const efficiencyFilter = useMemo(() => {
    return filterWasteWater.efficiency.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterWasteWater.efficiency]);

  const handleTableChange: TableProps<ResponseSettingWasteWaterTreatment>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseSettingWasteWaterTreatment>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => getIndex(index + 1),
      align: "center",
    },
    {
      title: "Emission Control",
      dataIndex: "emissionControl",
      key: "emissionControl",
      render: (text) => <Text>{text}</Text>,
      filters: emissionControlFilter,
      align: "left",
    },
    {
      title: "Efficiency",
      dataIndex: "efficiency",
      key: "efficiency",
      render: (text) => <Text>{formatNumberWithFillZero(text, 2)}</Text>,
      filters: efficiencyFilter,
      align: "left",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render: (text) => <Text>{text}</Text>,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  const getColumnFilter = () => {
    getFilterWasteWaterColumn();
  };

  useDidMount(() => {
    getColumnFilter();
    getSettingWasteWater({ ...filter, page, limit: pageSize });
  });

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddFlareInfo = () => {
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formSettingWasteWater.resetFields();
    clearErrorSettingWasteWater();
    setIsEdit(null);
  };

  const submitSettingFlareCompound = async () => {
    let checkError = false;
    await formSettingWasteWater.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = formSettingWasteWater.getFieldsValue();
    // console.log("form data ; ", formData);
    const formatData = replaceUndefinedWithNull(formData);
    const finalData = trimAllKey(formatData, ["emissionControl", "efficiency"]) as CreateSettingWasteWaterTreatment;
    if (isEdit) {
      updateSettingWasteWater({ ...finalData, _id: isEdit._id }).then(onFinishAction);
    } else createSettingWasteWater(finalData).then(onFinishAction);
  };

  const onFinishAction = (errorMessage?: unknown) => {
    // console.log("errorMessage : ", errorMessage);
    if (errorMessage) {
      formSettingWasteWater.setFields([
        {
          name: "emissionControl",
          errors: [""],
        },
        {
          name: "efficiency",
          errors: [""],
        },
      ]);
      return;
    } else {
      if (isEdit?._id) calculateReportVocAfterUpdateMasterWW(isEdit._id);
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getSettingWasteWater({ ...filter, page, limit: pageSize });
      getColumnFilter();
      closeModal();
    }
  };

  // console.log("filterFlareCompound : ", filterFlareCompound);

  return (
    <Row className="w-full" id={"wrap-combustion-config-table"}>
      {contextHolder}
      <Row className="w-full" justify={"end"}>
        {canEditTable(permissions) && (
          <Button
            type={"primary"}
            size={"middle"}
            className="flex items-center"
            onClick={onAddFlareInfo}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        )}
      </Row>
      <Table
        columns={columns}
        dataSource={sourceData as ResponseSettingWasteWaterTreatment[]}
        className="w-full"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <Modal
        title={<ModalTitle title="Emission Control" />}
        open={visibleModal}
        width={1000}
        onCancel={closeModal}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" loading={loadingManageWasteWater} onClick={submitSettingFlareCompound}>
              Save
            </Button>
          </Row>
        }
        maskClosable={false}
        centered
      >
        {errorManageSettingWater && (
          <Row className="w-full" justify={"center"}>
            <Text type="danger">{errorManageSettingWater}</Text>
          </Row>
        )}
        <Form
          layout={"vertical"}
          name={"setting-waste-water"}
          autoComplete="off"
          form={formSettingWasteWater}
          requiredMark={false}
          onFieldsChange={(_) => {
            if (errorManageSettingWater) {
              clearErrorSettingWasteWater();
              formSettingWasteWater.setFields([
                {
                  name: "emissionControl",
                  errors: undefined,
                },
                {
                  name: "efficiency",
                  errors: undefined,
                },
              ]);
            }
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Emission Control"} required />}
                name="emissionControl"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Emission Control"),
                  },
                ]}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Efficiency"} required />}
                name="efficiency"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Efficiency"),
                  },
                ]}
              >
                <InputNumber
                  max={100}
                  addonAfter={"%"}
                  step={1}
                  precision={2}
                  onKeyDown={(e) => {
                    onKeyDownNumberWithFixedDecimal(e, 2);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item label="Remark" name="remark">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
};

export default WasteWaterTable;
